import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ChatMessage } from 'types/Chat'
import { Maybe } from 'types/Maybe'
import {
  ChatViewportIntent,
  ChatViewportMetrics,
  ChatViewportSignal,
  createChatViewportState,
  decideChatViewport,
  isScrollToBottomVisible,
  TOP_OFFSET,
} from 'utils/chatViewport'

const getDistanceFromBottom = (container: HTMLElement) => (
  container.scrollHeight - container.clientHeight - container.scrollTop
)

const measure = (container: HTMLElement, turn: Maybe<HTMLElement>): ChatViewportMetrics => ({
  scrollTop: container.scrollTop,
  scrollHeight: container.scrollHeight,
  clientHeight: container.clientHeight,
  lastTurnHeight: turn?.offsetHeight,
})

interface ChatAnchorScrollOptions {
  chatId?: string
  loadedChatId?: string
  anchorMessageId?: string
  messages: ChatMessage[]
}

const useChatAnchorScroll = ({
  chatId,
  loadedChatId,
  anchorMessageId,
  messages,
}: ChatAnchorScrollOptions) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const lastTurnRef = useRef<HTMLDivElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef(createChatViewportState(chatId))
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const hasMessages = messages.length > 0

  const apply = useCallback((intent: ChatViewportIntent) => {
    const container = scrollRef.current
    const gutter = gutterRef.current
    const turn = lastTurnRef.current

    if (gutter && intent.gutterHeight !== undefined) {
      gutter.style.height = `${intent.gutterHeight}px`
    }
    if (container && intent.scroll) {
      const { to, behavior } = intent.scroll
      if (to === 'bottom' && behavior === 'auto') {
        container.scrollTop = container.scrollHeight
      } else if (to === 'bottom') {
        container.scrollTo({ top: container.scrollHeight, behavior })
      } else if (turn) {
        const top = container.scrollTop + turn.getBoundingClientRect().top
          - container.getBoundingClientRect().top - TOP_OFFSET
        container.scrollTo({ top, behavior })
      }
    }
    if (intent.showScrollToBottom !== undefined) {
      setShowScrollToBottom(intent.showScrollToBottom)
    } else if (container && intent.remeasureScrollToBottom) {
      setShowScrollToBottom(isScrollToBottomVisible(
        viewportRef.current,
        getDistanceFromBottom(container),
      ))
    }
  }, [])

  const send = useCallback((signal: ChatViewportSignal) => {
    const { state, intent } = decideChatViewport(viewportRef.current, signal)
    viewportRef.current = state
    apply(intent)
  }, [apply])

  const syncScroll = useCallback(() => {
    const container = scrollRef.current
    if (!container || !gutterRef.current) return
    send({ type: 'contentResized', metrics: measure(container, lastTurnRef.current) })
  }, [send])

  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return
    send({ type: 'scrollToBottomRequested' })
  }, [send])

  useLayoutEffect(() => {
    send({ type: 'chatChanged', chatId, hasAnchor: !!anchorMessageId })
  }, [anchorMessageId, chatId, send])

  useLayoutEffect(() => {
    if (!scrollRef.current || !chatId || loadedChatId !== chatId) return
    send({ type: 'chatLoaded', chatId })
  }, [chatId, loadedChatId, send])

  useLayoutEffect(() => {
    const container = scrollRef.current
    const turn = lastTurnRef.current
    if (!anchorMessageId || !container || !turn) return
    send({ type: 'messageSent', metrics: measure(container, turn) })
  }, [anchorMessageId, send])

  useLayoutEffect(() => {
    syncScroll()
  }, [messages, syncScroll])

  useEffect(() => {
    const container = scrollRef.current
    if (!hasMessages || !container) return undefined
    send({ type: 'attached', metrics: measure(container, lastTurnRef.current) })

    const onScroll = () => {
      send({ type: 'scrolled', metrics: measure(container, lastTurnRef.current) })
    }

    const observer = new ResizeObserver(syncScroll)
    observer.observe(container)
    if (container.firstElementChild) observer.observe(container.firstElementChild)
    container.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      observer.disconnect()
      container.removeEventListener('scroll', onScroll)
    }
  }, [hasMessages, send, syncScroll])

  return {
    scrollRef,
    lastTurnRef,
    gutterRef,
    showScrollToBottom,
    scrollToBottom,
  }
}

export { useChatAnchorScroll }
